import { FC, useEffect, useRef, useState } from 'react'
import ValidatorWrapper, { rules, ValidatorField } from '@coxy/react-validator'
import cn from 'classnames'

import { constants } from 'config/constants'
import validatorRules from 'libs/validator-rules'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { changePhone } from 'store/fetchers/users'
import { fetchUser, selectedUser } from 'store/reducers/users'
import {
  EMessageTypes,
  getVerifiedToken,
  resendVerificationCode,
  sendVerificationCode,
  sendVerificationCodeRateLimited,
} from 'store/fetchers/utils'
import InputPhone from 'components/app/input-phone'
import { Captcha } from 'components/app/captcha'
import InputCode from 'components/app/input-code'
import ButtonBlue from 'components/ui/button/_color/_blue'
import CellHeading from 'components/ui/cell/__heading'
import { GoogleCheck2faCode } from 'components/ui/google-check-2fa-code'
import Popup from 'components/ui/popup'

import styles from './styles.module.scss'
import { useTranslation } from 'utils/use-translation'

export const ChangePhonePopup: FC<{
  closePopup: () => void
}> = ({ closePopup }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const validator = useRef<ValidatorWrapper>()
  const phoneVerifiedToken = useRef('')
  const phoneVerificationCode = useRef('')
  const newPhoneVerificationCode = useRef('')
  const isActive2faRef = useRef(false)
  const [captcha, setCaptcha] = useState<string>('')

  const [isLoading, _setIsLoading] = useState(false)
  const [phone, _setPhone] = useState('')

  const isLoadingRef = useRef(isLoading)
  const phoneRef = useRef(phone)

  const user = useAppSelector(selectedUser)

  const [step, setStep] = useState<1 | 2 | 3 | 4>(1)
  const [isVisibleErrors, setIsVisibleErrors] = useState(false)
  const [isPhoneValid, setIsPhoneValid] = useState<boolean>(null)
  const [phoneCode, setPhoneCode] = useState('')
  const [newPhoneCode, setNewPhoneCode] = useState('')
  const [isPhoneCodeValid, setIsPhoneCodeValid] = useState<boolean>(null)
  const [isNewPhoneCodeValid, setIsNewPhoneCodeValid] = useState<boolean>(null)
  const [isValid2faCode, setIsValid2faCode] = useState(null)
  const [newPhone, _setNewPhone] = useState<string>('')
  const [isPhoneBtnDisabled, setIsPhoneBtnDisabled] = useState<boolean>(false)
  const [isNewPhoneBtnDisabled, setIsNewPhoneBtnDisabled] = useState<boolean>(false)
  const [phoneCodeErrorCode, setPhoneCodeErrorCode] = useState<string>(null)
  const [newPhoneCodeErrorCode, setNewPhoneCodeErrorCode] = useState<string>(null)
  const [gaCodeErrorCode, setGaCodeErrorCode] = useState<string>(null)
  const [phoneErrorMessage, setPhoneErrorMessage] = useState<string>(null)

  const setIsLoading = (value: boolean): void => {
    isLoadingRef.current = value
    _setIsLoading(value)
  }

  const setPhone = (value: string): void => {
    phoneRef.current = value
    _setPhone(value)
  }

  const verificationCode = phoneVerificationCode.current

  const resendPhoneCode = async (captcha: string): Promise<void> => {
    const token = await resendVerificationCode({
      verification_token: verificationCode,
      captcha: captcha,
    })

    phoneVerificationCode.current = token
  }

  const validatePhone = async (captcha: string): Promise<boolean> => {
    if (!phoneRef.current) {
      setIsPhoneValid(false)
      setIsVisibleErrors(true)
      return false
    }

    setIsLoading(true)
    setIsVisibleErrors(false)

    const { tokens, isActive2fa, error } = await sendVerificationCode({
      ...{ phone: phoneRef.current, newPhone: newPhone },
      captcha: captcha,
      type: EMessageTypes.CHANGE_PHONE,
    })
    setPhoneErrorMessage(error)
    setCaptcha(captcha)
    isActive2faRef.current = isActive2fa
    phoneVerificationCode.current = tokens[0]

    const result = !!phoneVerificationCode.current

    if (result) {
      setStep(2)
    }

    setIsVisibleErrors(!result)
    setIsPhoneValid(result)
    setIsLoading(false)

    return result
  }

  const validatePhoneCode = async () => {
    if (phoneCode?.length !== constants.codeLength) {
      setIsPhoneCodeValid(false)
      setIsVisibleErrors(true)
      return
    }

    try {
      const res = await getVerifiedToken({
        code: phoneCode,
        verification_token: phoneVerificationCode.current,
      })
      if (res.result) {
        phoneVerifiedToken.current = res.response
        setIsVisibleErrors(!phoneVerifiedToken.current)
        setIsPhoneCodeValid(!!phoneVerifiedToken.current)
      } else {
        setIsVisibleErrors(true)
        setIsPhoneCodeValid(false)
      }
    } catch (err) {
      setIsPhoneCodeValid(false)
      setIsVisibleErrors(true)
      setIsPhoneBtnDisabled(true)

      const msg = err?.response?.message || err?.response?.error || ''

      if (err.response && msg?.includes('limit')) {
        setPhoneCodeErrorCode(t('rate_limit_exceeded_retry_later'))
      } else {
        setPhoneCodeErrorCode('INVALID_REQUEST_PARAMS')
      }
      setIsLoading(false)
    }
  }

  const validateCodes = async (): Promise<void> => {
    if (isLoadingRef.current) {
      return
    }

    setIsLoading(true)

    await Promise.all([validatePhoneCode()])

    setIsLoading(false)
  }

  const validateNewCodes = async (): Promise<void> => {
    if (isLoadingRef.current) {
      return
    }

    setIsLoading(true)

    await Promise.all([validateNewPhoneCode()])

    setIsLoading(false)
  }

  const validateNewPhoneCode = async () => {
    if (newPhoneCode?.length !== constants.codeLength) {
      setIsNewPhoneCodeValid(false)
      setIsVisibleErrors(true)
      return
    }

    try {
      const res = await getVerifiedToken({
        code: newPhoneCode,
        verification_token: newPhoneVerificationCode.current,
      })
      if (res.result) {
        phoneVerifiedToken.current = res.response
        setIsVisibleErrors(!phoneVerifiedToken.current)
        setIsNewPhoneCodeValid(!!phoneVerifiedToken.current)
      } else {
        setIsVisibleErrors(true)
        setIsNewPhoneCodeValid(false)
      }
    } catch (err) {
      setIsNewPhoneCodeValid(false)
      setIsVisibleErrors(true)
      setIsNewPhoneBtnDisabled(true)

      const msg = err?.response?.message || err?.response?.error || ''

      if (err.response && msg?.includes('limit')) {
        setNewPhoneCodeErrorCode(t('rate_limit_exceeded_retry_later'))
      }
      setNewPhoneCodeErrorCode('INVALID_REQUEST_PARAMS')
      setIsLoading(false)
    }
  }

  const confirmUpdate = async (): Promise<void> => {
    if (!isPhoneCodeValid) {
      return
    }

    if (isActive2faRef.current) {
      validateNewPhone()
      setStep(3)
    }
  }

  const onCode2faSubmit = async (code2fa) => {
    setIsLoading(true)
    try {
      const result = await changePhone({
        verification_token: phoneVerifiedToken.current,
        new_phone: newPhone,
        code_2fa: code2fa,
      })

      if (result) {
        await dispatch(fetchUser())
        setGaCodeErrorCode(null)
        setIsValid2faCode(true)
        setIsVisibleErrors(false)
        closePopup()
      } else {
        setGaCodeErrorCode('INVALID_REQUEST_PARAMS')
        setIsValid2faCode(false)
        setIsVisibleErrors(true)
      }
    } catch {
      setGaCodeErrorCode('INVALID_REQUEST_PARAMS')
      setIsValid2faCode(false)
      setIsVisibleErrors(true)
    } finally {
      setIsLoading(false)
    }
  }

  const onChangePhone = async () => {
    if (!isNewPhoneCodeValid) {
      return
    }

    if (isActive2faRef.current) {
      setStep(4)
    }
  }

  const handleChange2FAValue = (value: string) => {
    if (value.length !== 6) {
      setIsVisibleErrors(false)
      setGaCodeErrorCode(null)
    }
  }

  const validateNewPhone = async () => {
    if (!newPhone) {
      setIsPhoneValid(false)
      setIsVisibleErrors(true)
      return false
    }

    setIsLoading(true)
    setIsVisibleErrors(false)

    const { tokens, isActive2fa } = await sendVerificationCodeRateLimited({
      ...{ phone: newPhone, newPhone: newPhone },
      type: EMessageTypes.CHANGE_PHONE,
    })
    setCaptcha(captcha)
    isActive2faRef.current = isActive2fa
    newPhoneVerificationCode.current = tokens[0]

    const result = !!newPhoneVerificationCode.current

    setIsVisibleErrors(!result)
    setIsPhoneValid(result)
    setIsLoading(false)

    return result
  }

  useEffect(() => {
    setIsPhoneCodeValid(null)
    setIsPhoneBtnDisabled(false)
    setIsVisibleErrors(false)
    setPhoneCodeErrorCode(null)

    if (phoneCode?.length !== constants.codeLength || isLoadingRef.current) {
      return
    }

    async function runValidateEmailCode() {
      setIsLoading(true)
      await validatePhoneCode()
      setIsLoading(false)
    }

    void runValidateEmailCode()
  }, [phoneCode])

  useEffect(() => {
    setIsNewPhoneCodeValid(null)
    setIsNewPhoneBtnDisabled(false)
    setIsVisibleErrors(false)
    setNewPhoneCodeErrorCode(null)

    if (phoneCode?.length !== constants.codeLength || isLoadingRef.current) {
      return
    }

    async function runValidateEmailCode() {
      setIsLoading(true)
      await validateNewPhoneCode()
      setIsLoading(false)
    }

    void runValidateEmailCode()
  }, [newPhoneCode])

  useEffect(() => {
    if (user) {
      setPhone(user.phone)
    }
  }, [user])

  return (
    <ValidatorWrapper ref={validator}>
      <Popup onClose={closePopup} heading={t('change_phone')} subtitle={t('access_account_convinient')}>
        {step === 1 && (
          <>
            <div className={styles.phoneEmail}>
              <div className={styles.fieldWrapper}>
                <ValidatorField value={phone} ru={validatorRules.bool}>
                  <CellHeading className={styles.input__label}>{t('phone_to_change')}</CellHeading>
                  <InputPhone
                    value={newPhone}
                    onChange={(value) => _setNewPhone(value)}
                    isValid={isPhoneValid}
                    isVisibleError={isVisibleErrors}
                    errorMessage={phoneErrorMessage}
                  />
                </ValidatorField>
              </div>
            </div>
            <div className={cn(styles.captchaWrapper, styles.verifyButtonContainer)}>
              <Captcha onCaptchaSuccess={validatePhone} setIsLoading={setIsLoading}>
                <ButtonBlue className={styles.verifyButton} isLoading={isLoading}>{t('verify')}</ButtonBlue>
              </Captcha>
            </div>
          </>
        )}

        {step === 2 && (
          <div className={styles.codes}>
            <div className={styles.codeRequestWrapper}>
              <div className={styles.fieldWrapper}>
                <ValidatorField value={phoneCode} rules={rules.bool} />
                <CellHeading className={styles.input__label}>{t('enter_code_sent_to')} {user.phone}</CellHeading>
                <InputCode
                  onEnter={validateCodes}
                  isValid={isPhoneCodeValid}
                  value={phoneCode}
                  onChange={(code: string) => {
                    setPhoneCode(code)
                    setPhoneCodeErrorCode(null)
                    setIsVisibleErrors(false)
                    setIsPhoneBtnDisabled(false)
                  }}
                  onResendCode={resendPhoneCode}
                  isVisibleError={isVisibleErrors}
                  disabled={isPhoneCodeValid === true}
                  errorCode={phoneCodeErrorCode}
                />
              </div>
            </div>

            <div className={cn(styles.captchaWrapper, styles.verifyButtonContainer)}>
              <ButtonBlue disabled={isPhoneBtnDisabled} className={styles.verifyButton} onClick={confirmUpdate}>{t('confirm')}</ButtonBlue>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className={styles.codes}>
            <div className={styles.codeRequestWrapper}>
              <div className={styles.fieldWrapper}>
                <ValidatorField value={newPhoneCode} rules={rules.bool} />
                <CellHeading className={styles.input__label}>{t('enter_code_sent_to')} {newPhone}</CellHeading>
                <InputCode
                  onEnter={validateNewCodes}
                  isValid={isNewPhoneCodeValid}
                  value={newPhoneCode}
                  onChange={(code: string) => {
                    setNewPhoneCode(code)
                    setNewPhoneCodeErrorCode(null)
                    setIsVisibleErrors(false)
                    setIsNewPhoneBtnDisabled(false)
                  }}
                  onResendCode={resendPhoneCode}
                  isVisibleError={isVisibleErrors}
                  disabled={isNewPhoneCodeValid === true}
                  errorCode={newPhoneCodeErrorCode}
                />
              </div>
            </div>

            <div className={cn(styles.captchaWrapper, styles.verifyButtonContainer)}>
              <ButtonBlue disabled={isNewPhoneBtnDisabled} className={styles.verifyButton} onClick={onChangePhone}>{t('confirm')}</ButtonBlue>
            </div>
          </div>
        )}

        {step === 4 && (
          <GoogleCheck2faCode
            classNameInputCode={styles.inputCode2fa}
            onSubmit={onCode2faSubmit}
            isCodeValid={isValid2faCode}
            isLoading={isLoading}
            setIsValidCode={(v) => setIsValid2faCode(v)}
            errorCode={gaCodeErrorCode}
            isVisibleError={isVisibleErrors}
            onChangeValue={handleChange2FAValue}
          />
        )}
      </Popup>
    </ValidatorWrapper>
  )
}
